import ColGrip from '../components/ColGrip';
import React, { useState, useMemo } from 'react';
import { useTenantData } from '../context/TenantDataContext';
import { useCachedFetch } from '../hooks/useCachedFetch';
import SearchInput from '../components/SearchInput';
import TrailFrame, { TrailTabs, TrailPageLoading } from '../components/TrailFrame';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { areaLabel } from '../data/areaLabels';
import { buildTimelines } from '../utils/historyTimeline';
import './styles.css';
import './WorkQueueGroups.css';
import './WorkQueueCards.css';
import './CompliancePage.css';
import './History.css';

const AREA_LABEL = { overall: 'Samlet status', identity: 'Brugere', devices: 'Enheder', applications: 'Apps', exchange: 'E-mail', other: 'Andet' };
// Historik is a log, like Sign-in logs in Entra: one row per event, newest first, a click opens the details. It only holds what
// Gennemgang cannot: what is resolved, what could not be confirmed, and what has changed. Open findings live in Gennemgang.
const TABS = [['res', 'Løst'], ['delta', 'Ændringer'], ['unc', 'Ikke bekræftet']];

// Two sources feed this page: the daily ledger of new and gone findings (/api/findings-ledger) and the day-over-day figures
// (/api/history). A raw Microsoft Graph directory-audit feed used to be a source too, but it logs routine noise at a volume that
// buried what matters, so it was dropped.
function classifyDeltaEntry(text) {
  if (text.startsWith('MFA-dækning')) return 'identity';
  if (text.startsWith('Andelen af compliant enheder')) return 'devices';
  if (text.startsWith('Antal Global Administrators')) return 'identity';
  return 'overall';
}

const clockOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : '');
const dayMonth = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
const withClock = (date, at) => `${dayMonth(date)}${at ? ` kl. ${clockOf(at)}` : ''}`;
const dayMonthYear = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
const daysText = (n) => (n === 0 ? 'under 1 dag' : n === 1 ? '1 dag' : `${n} dage`);
const todayYmd = () => new Date().toISOString().slice(0, 10);

// What one row says: its status, how long it lasted, and the days it was found and resolved.
function rowTexts(t) {
  const found = t.openedKnown && t.openedOn ? `${dayMonthYear(t.openedOn)}` : 'Før perioden';
  if (t.state === 'res') {
    return { status: 'Løst', span: t.openedKnown ? daysText(t.days) : 'Før perioden', found, resolved: dayMonthYear(t.lastDay) };
  }
  return { status: 'Ikke bekræftet', span: '—', found, resolved: 'Ikke bekræftet' };
}
export default function History() {
  const { tenant } = useTenantData();
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState('res');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const detailPanel = useResizablePanel(528, 340, 1000);
  // Cached so navigating away and back does not re-flash the loading state.
  const { data: history, error: historyError } = useCachedFetch(tenant ? `/api/history?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const { data: ledger, error: ledgerError } = useCachedFetch(tenant ? `/api/findings-ledger?tenantId=${tenant.tenantId}&days=${days <= 30 ? 30 : days}` : null, { skip: !tenant });

  const today = todayYmd();
  const from = useMemo(() => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10), [days]);

  const timelines = useMemo(() => buildTimelines((ledger?.events || []).filter((e) => e.d >= from), { today, from }), [ledger, today, from]);
  const deltas = useMemo(() => (history?.entries || []).filter((e) => e.date >= from).map((e, i) => {
    const areaKey = classifyDeltaEntry(e.text);
    return { id: `delta-${i}`, date: e.date, tone: e.tone === 'bad' ? 'bad' : e.tone === 'warn' ? 'warn' : 'ok', title: e.text, area: AREA_LABEL[areaKey] };
  }).sort((a, b) => b.date.localeCompare(a.date)), [history, from]);

  const counts = {
    res: timelines.filter((t) => t.state === 'res').length,
    unc: timelines.filter((t) => t.state === 'unc').length,
    delta: deltas.length,
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (tab === 'delta') return deltas.filter((e) => !q || e.title.toLowerCase().includes(q) || e.area.toLowerCase().includes(q));
    return timelines.filter((t) => t.state === tab && (!q || t.name.toLowerCase().includes(q) || t.title.toLowerCase().includes(q) || areaLabel(t.area).toLowerCase().includes(q)));
  }, [timelines, deltas, tab, search]);

  if (historyError) return <p className="state-error">{historyError}</p>;
  const lead = 'Det, der er løst, og det, der har ændret sig. Åbn en række for at se detaljerne.';
  // A ledger that cannot be read leaves the page without the new and gone rows, never without the figures.
  if (!history || (!ledger && !ledgerError)) return <TrailPageLoading current="history" text={lead} panelWidth={detailPanel.width} />;

  const selected = filtered.find((e) => (tab === 'delta' ? e.id : e.key) === selectedId) || null;
  const foot = selected && selected.state === 'unc'
    ? 'Et fund står kun som løst, når objektet er skiftet til Compliant. Er en enhed forsvundet fra Intune, kan vi ikke bekræfte det. Står en enhed som Not evaluated, kræver det opfølgning i Intune.'
    : 'Et fund står kun som løst, når en vellykket synkronisering har bekræftet det, og det stadig er væk ved den næste synkronisering mindst en halv time senere. Kunne data ikke hentes, eller kommer det tilbage, står det ikke som løst. Fund, der bygger på et vindue af login-hændelser, fx mislykkede login, kan aldrig stå som løst. Kommer noget tilbage inden for en uge, regnes det som samme forløb.';
  return (
    <div className="cmp dc-page">
      <div className="head tf-head">
        <div>
          <div className="eye">Dokumentation</div>
          <h1>Revisionsspor</h1>
          <p>{lead}</p>
        </div>
      </div>
      <TrailFrame
        current="history"
        panelWidth={detailPanel.width}
        tabs={<TrailTabs label="Emne" current={tab} onChange={(k) => { setTab(k); setSelectedId(null); }} items={TABS.map(([key, label]) => ({ key, label, count: counts[key], warm: key === 'unc' && counts[key] > 0 }))} />}
        right={(
          <>
            <div className="hx2-per" role="group" aria-label="Periode">
              <button type="button" className={days === 7 ? 'on' : ''} aria-pressed={days === 7} onClick={() => setDays(7)}>7 dage</button>
              <button type="button" className={days === 30 ? 'on' : ''} aria-pressed={days === 30} onClick={() => setDays(30)}>30 dage</button>
              <button type="button" className={days === 90 ? 'on' : ''} aria-pressed={days === 90} onClick={() => setDays(90)}>3 måneder</button>
              <button type="button" className={days === 365 ? 'on' : ''} aria-pressed={days === 365} onClick={() => setDays(365)}>12 måneder</button>
            </div>
            <SearchInput value={search} onChange={setSearch} placeholder="Søg i objekter og fund ..." />
          </>
        )}
      >
        <div className="workspace">
          <div className="list-panel list-panel--float wq-menu">
            <div className="wq-tablecard hk-list">
              {filtered.length === 0 ? (
                <div className="state-empty"><p>{tab === 'unc' ? 'Intet er ubekræftet. Alt, der er forsvundet, er bekræftet løst.' : 'Intet i perioden.'}</p></div>
              ) : (
                <div className="hk-tbl" role="table">
                  <div className={`hk-tr hk-tr--h${tab === 'delta' ? ' hk-tr--d' : ''}`} role="row">
                    <span>Dato og tid<ColGrip /></span>
                    {tab === 'delta' ? <><span>Ændring<ColGrip /></span><span>Område<ColGrip /></span></> : <><span>Objekt<ColGrip /></span><span>Fund<ColGrip /></span><span>Område<ColGrip /></span><span>Status<ColGrip /></span><span>Varede<ColGrip /></span></>}
                  </div>
                  {filtered.map((e) => {
                    if (tab === 'delta') {
                      return (
                        <div key={e.id} role="row" tabIndex={0} className={`hk-tr hk-tr--d${selected?.id === e.id ? ' is-selected' : ''}`} onClick={() => setSelectedId(e.id)} onKeyDown={(ev) => { if (ev.key === 'Enter') setSelectedId(e.id); }}>
                          <span className="hk-m">{dayMonth(e.date)}</span>
                          <span title={e.title}>{e.title}</span>
                          <span className="hk-m">{e.area}</span>
                        </div>
                      );
                    }
                    const x = rowTexts(e);
                    return (
                      <div key={e.key} role="row" tabIndex={0} className={`hk-tr${selected?.key === e.key ? ' is-selected' : ''}`} onClick={() => setSelectedId(e.key)} onKeyDown={(ev) => { if (ev.key === 'Enter') setSelectedId(e.key); }}>
                        <span className="hk-m">{withClock(e.lastDay, e.lastAt)}</span>
                        <b title={e.name}>{e.group ? `${e.n} objekter` : e.name}</b>
                        <span title={e.title}>{e.title}</span>
                        <span className="hk-m">{areaLabel(e.area)}</span>
                        <span className={`hk-st${e.state === 'unc' ? ' hk-st--unc' : ''}`}><i />{x.status}</span>
                        <span className="hk-m">{x.span}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className={`resize-handle${detailPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={detailPanel.onMouseDown} />
          <div className="detail-panel-col" style={{ width: detailPanel.width }}>
            <div className="detail-panel">
              {selected && tab === 'delta' ? (
                <div className="hk-pn">
                  <div className="hk-pn__h"><div><h3>{selected.title}</h3><p>{selected.area}</p></div></div>
                  <dl className="hk-kv"><dt>Status</dt><dd>Ændring</dd><dt>Dato</dt><dd>{dayMonthYear(selected.date)}</dd><dt>Område</dt><dd>{selected.area}</dd></dl>
                </div>
              ) : selected ? (
                <div className="hk-pn">
                  <div className="hk-pn__h"><div><h3>{selected.group ? selected.title : selected.name}</h3><p>{`${selected.group ? `${selected.n} objekter` : selected.title} · ${areaLabel(selected.area)}`}</p></div></div>
                  <dl className="hk-kv">
                    <dt>Status</dt><dd><span className={`hk-st${selected.state === 'unc' ? ' hk-st--unc' : ''}`}><i />{rowTexts(selected).status}</span></dd>
                    <dt>{selected.group ? 'Objekter' : 'Objekt'}</dt><dd>{selected.group ? `${selected.n}` : selected.name}</dd>
                    <dt>Fund</dt><dd>{selected.title}</dd>
                    <dt>Område</dt><dd>{areaLabel(selected.area)}</dd>
                    <dt>Opdaget</dt><dd>{rowTexts(selected).found}</dd>
                    <dt>{selected.state === 'res' ? 'Løst' : 'Sidst set'}</dt><dd>{selected.state === 'res' ? rowTexts(selected).resolved : dayMonthYear(selected.lastDay)}</dd>
                    {selected.state === 'res' && <><dt>Varede</dt><dd>{rowTexts(selected).span}</dd></>}
                  </dl>
                  <p className="hk-pn__p">{selected.state === 'res' ? (selected.openedKnown ? `Fundet var åbent i ${daysText(selected.days)} og er nu rettet.` : 'Fundet var åbent før perioden begyndte og er nu rettet.') : 'Fundet er forsvundet fra målingen, men vi kan ikke bekræfte, at det er rettet.'}</p>
                  <p className="hk-pn__f">{foot}</p>
                  {ledger?.skip && <p className="hk-pn__f">Nogle datakilder kan ikke læses fuldt ud lige nu, så løste fund opdateres ikke, før de kan.</p>}
                </div>
              ) : (
                <div className="wq-empty">
                  <div className="wq-empty__slot">
                    <span className="wq-empty__ic" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></span>
                    <b>Vælg en række</b>
                    <small>Forløbet, og hvad der gælder nu, vises her.</small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </TrailFrame>
    </div>
  );
}
